/** Typed fetchers for the dashboard API. All requests are same-origin. */

export interface MeMembership {
  tenantId: string;
  slug: string;
  name: string;
  role: string;
}

export interface Me {
  user: { id: string; email: string; name: string; isSuperAdmin: boolean };
  memberships: MeMembership[];
}

/**
 * Thrown for transient/server errors so callers can distinguish "logged out"
 * (fetchMe resolves to null) from "the request failed" (this throws) and show
 * a retry state instead of bouncing the user to sign-in.
 */
export class ApiError extends Error {
  constructor(public readonly status: number) {
    super(`API request failed: ${status}`);
    this.name = 'ApiError';
  }
}

/** Returns the current user + stores, or null when not signed in. */
export async function fetchMe(): Promise<Me | null> {
  const res = await fetch('/api/me', { headers: { accept: 'application/json' } });
  if (res.status === 401) return null;
  if (!res.ok) throw new ApiError(res.status);
  return (await res.json()) as Me;
}

export interface CreateStoreResult {
  ok: boolean;
  slug?: string;
  error?: string;
}

export async function createStore(input: {
  name: string;
  slug: string;
}): Promise<CreateStoreResult> {
  const res = await fetch('/api/tenant', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = (await res.json().catch(() => ({}))) as { slug?: string; error?: string };
  return { ok: res.ok, slug: data.slug, error: data.error };
}

/** Seed the demo stores (Acme Appliances + Acme Baby; super-admin only). */
export async function seedDemo(): Promise<{ slug: string }> {
  const res = await fetch('/api/dev/seed', { method: 'POST' });
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(b.error ?? `seed failed: ${res.status}`);
  }
  const data = (await res.json()) as { appliances: { slug: string } };
  // Land on the appliances store after seeding.
  return { slug: data.appliances.slug };
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

/* -------------------------------- Agents --------------------------------- */

export type PersonaArchetype = 'concierge' | 'practical';
export type OverrideMode = 'inherit' | 'augment' | 'overwrite';

export interface AgentSummary {
  id: string;
  expertise: string;
  personaArchetype: PersonaArchetype | null;
  enabled: boolean;
  isUnlinked: boolean;
  skuScopeMode: 'categories' | 'whitelist';
  templateId: string | null;
  templateName: string | null;
  suggestedPrompts: string[];
}

export interface TemplateMeta {
  id: string;
  name: string;
  defaultExpertise: string;
  personaArchetype: PersonaArchetype | null;
}

export interface AgentFieldConfig {
  field: string;
  mode: OverrideMode;
  value: string;
}

export interface AgentDetail {
  agent: {
    id: string;
    expertise: string;
    personaArchetype: PersonaArchetype | null;
    enabled: boolean;
    isUnlinked: boolean;
    skuScopeMode: 'categories' | 'whitelist';
    templateId: string | null;
    suggestedPrompts: string[];
  };
  fields: AgentFieldConfig[];
}

const base = (slug: string) => `/api/admin/${encodeURIComponent(slug)}`;

export async function listAgents(slug: string): Promise<AgentSummary[]> {
  const data = await jsonOrThrow<{ agents: AgentSummary[] }>(await fetch(`${base(slug)}/agents`));
  return data.agents;
}

export async function listTemplates(slug: string): Promise<TemplateMeta[]> {
  const data = await jsonOrThrow<{ templates: TemplateMeta[] }>(
    await fetch(`${base(slug)}/templates`),
  );
  return data.templates;
}

export async function createAgent(
  slug: string,
  input: {
    expertise: string;
    templateId?: string | null;
    personaArchetype?: PersonaArchetype | null;
    isUnlinked?: boolean;
  },
): Promise<{ id: string }> {
  return jsonOrThrow<{ id: string }>(
    await fetch(`${base(slug)}/agents`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

export async function getAgent(slug: string, id: string): Promise<AgentDetail> {
  return jsonOrThrow<AgentDetail>(await fetch(`${base(slug)}/agents/${id}`));
}

export async function updateAgent(
  slug: string,
  id: string,
  patch: Record<string, unknown>,
): Promise<void> {
  await jsonOrThrow(
    await fetch(`${base(slug)}/agents/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(patch),
    }),
  );
}

export async function setAgentOverride(
  slug: string,
  id: string,
  field: string,
  mode: OverrideMode,
  value: string,
): Promise<void> {
  await jsonOrThrow(
    await fetch(`${base(slug)}/agents/${id}/overrides`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ field, mode, value }),
    }),
  );
}

/* --------------------- Super-admin template editor ----------------------- */

export interface TemplateStats {
  agents: number;
  stores: number;
  conversations: number;
  engaged: number;
  recsShown: number;
  recsClicked: number;
  feedbackUp: number;
  feedbackDown: number;
}

export interface SuperTemplate {
  id: string;
  templateKey: string;
  name: string;
  defaultExpertise: string;
  personaArchetype: PersonaArchetype | null;
  enabled: boolean;
  /** Cross-tenant usage + performance (super list only). */
  stats?: TemplateStats;
}

export async function superListTemplates(): Promise<SuperTemplate[]> {
  const data = await jsonOrThrow<{ templates: SuperTemplate[] }>(
    await fetch('/api/super/templates'),
  );
  return data.templates;
}

export async function superGetTemplate(
  id: string,
): Promise<{ template: SuperTemplate; fields: { field: string; content: string }[] }> {
  return jsonOrThrow(await fetch(`/api/super/templates/${id}`));
}

export async function superCreateTemplate(input: {
  templateKey: string;
  name: string;
  defaultExpertise?: string;
  personaArchetype?: PersonaArchetype | null;
}): Promise<{ id: string }> {
  return jsonOrThrow<{ id: string }>(
    await fetch('/api/super/templates', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

export async function superSetTemplateField(
  id: string,
  field: string,
  content: string,
): Promise<void> {
  await jsonOrThrow(
    await fetch(`/api/super/templates/${id}/fields`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ field, content }),
    }),
  );
}

/* -------------------------------- Catalog -------------------------------- */

export interface Category {
  id: string;
  name: string;
  slug: string;
}

export interface Product {
  id: string;
  sku: string;
  title: string;
  brand: string | null;
  priceCents: number | null;
  currency: string;
  inStock: boolean;
  categoryId: string | null;
  attributesJson: string | null;
  signatureHash: string | null;
  mappingGroupId: string | null;
}

export async function listCategories(slug: string): Promise<Category[]> {
  const data = await jsonOrThrow<{ categories: Category[] }>(
    await fetch(`${base(slug)}/catalog/categories`),
  );
  return data.categories;
}

export async function createCategory(slug: string, name: string): Promise<{ id: string }> {
  return jsonOrThrow<{ id: string }>(
    await fetch(`${base(slug)}/catalog/categories`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name }),
    }),
  );
}

export async function listProducts(slug: string, categoryId?: string): Promise<Product[]> {
  const qs = categoryId ? `?category=${encodeURIComponent(categoryId)}` : '';
  const data = await jsonOrThrow<{ products: Product[] }>(
    await fetch(`${base(slug)}/catalog/products${qs}`),
  );
  return data.products;
}

export async function importFeed(
  slug: string,
  text: string,
  kind?: 'csv' | 'json',
): Promise<{ imported: number }> {
  return jsonOrThrow<{ imported: number }>(
    await fetch(`${base(slug)}/catalog/import`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ text, kind }),
    }),
  );
}

/* ------------------------------ Agent scope ------------------------------ */

export interface AgentScope {
  categoryIds: string[];
  exclusionProductIds: string[];
  inclusionProductIds: string[];
}

export async function getAgentScope(slug: string, agentId: string): Promise<AgentScope> {
  const data = await jsonOrThrow<{ scope: AgentScope }>(
    await fetch(`${base(slug)}/agents/${agentId}/scope`),
  );
  return data.scope;
}

export async function setAgentScope(
  slug: string,
  agentId: string,
  scope: Partial<AgentScope>,
): Promise<void> {
  await jsonOrThrow(
    await fetch(`${base(slug)}/agents/${agentId}/scope`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(scope),
    }),
  );
}

/* -------------------------------- Sandbox -------------------------------- */

export interface SandboxRecommendation {
  productId: string;
  sku: string;
  title: string;
  priceCents: number | null;
  priceUpdatedAt: number | null;
  position: number;
  reason: string;
}

export interface SandboxTurn {
  conversationId: string;
  reply: string;
  agentId: string | null;
  routed: boolean;
  recommended: boolean;
  recommendations: SandboxRecommendation[];
  mustHaves: { text: string; inSpecs: boolean }[];
  considerationSetEmpty: boolean;
  relaxationImpacts: string[];
}

export async function sandboxTurn(
  slug: string,
  message: string,
  conversationId?: string,
  agentId?: string,
): Promise<SandboxTurn> {
  return jsonOrThrow<SandboxTurn>(
    await fetch(`${base(slug)}/sandbox/turn`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ message, conversationId, agentId }),
    }),
  );
}

/* ------------------------------ Documents -------------------------------- */

export interface ProductDocument {
  id: string;
  kind: string;
  filename: string | null;
  status: string;
}

export interface KnowledgeItem {
  id: string;
  topic: string;
  heading: string | null;
  content: string;
}

export async function listProductDocuments(
  slug: string,
  productId: string,
): Promise<{ documents: ProductDocument[]; knowledge: KnowledgeItem[] }> {
  return jsonOrThrow(
    await fetch(`${base(slug)}/documents?product=${encodeURIComponent(productId)}`),
  );
}

export async function ingestDocument(
  slug: string,
  input: { productId: string; text: string; kind?: 'manual' | 'setup_guide' | 'spec_sheet' },
): Promise<{ documentId: string; attributes: Record<string, unknown>; knowledgeCount: number }> {
  return jsonOrThrow(
    await fetch(`${base(slug)}/documents`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

/* ----------------------------- Conversations ----------------------------- */

export interface ConversationSummary {
  id: string;
  startedAt: number;
  expertiseRouted: string | null;
  reachedRecommendation: boolean;
  costMicros: number;
  /** True when the whole conversation ran on local Ollama (cost is notional). */
  local: boolean;
  models: string[];
}

export interface TranscriptMessage {
  id: string;
  role: 'user' | 'assistant' | 'system_internal';
  agent: string | null;
  content: string;
  turnIndex: number;
  feedback: number | null;
  recommendations: {
    position: number;
    sku: string;
    title: string;
    reason: string;
    clicked: boolean;
  }[];
}

export interface ConversationDetail {
  conversation: {
    id: string;
    startedAt: number;
    expertiseRouted: string | null;
    reachedRecommendation: boolean;
  };
  messages: TranscriptMessage[];
  cost: {
    totalMicros: number;
    local: boolean;
    models: string[];
    breakdown?: Record<string, number>;
  };
}

export async function listConversations(slug: string): Promise<ConversationSummary[]> {
  const data = await jsonOrThrow<{ conversations: ConversationSummary[] }>(
    await fetch(`${base(slug)}/conversations`),
  );
  return data.conversations;
}

export async function getConversation(slug: string, id: string): Promise<ConversationDetail> {
  return jsonOrThrow<ConversationDetail>(await fetch(`${base(slug)}/conversations/${id}`));
}

/** Format integer micros (1e-6 USD) as a dollar string. */
export function formatMicros(micros: number): string {
  return `$${(micros / 1_000_000).toFixed(4)}`;
}

/* ------------------------------- Analytics ------------------------------- */

export type AnalyticsRange = '7d' | '30d' | '90d';

export interface AnalyticsSummary {
  rangeDays: number;
  conversations: number;
  engagementRate: number;
  clickThroughRate: number;
  recsShown: number;
  recsClicked: number;
  avgCostMicros: number;
  totalCostMicros: number;
  feedbackUp: number;
  feedbackDown: number;
  topProducts: { sku: string; title: string; shown: number; clicked: number }[];
  daily: { dayMs: number; conversations: number }[];
}

export async function getAnalytics(
  slug: string,
  range: AnalyticsRange = '30d',
): Promise<AnalyticsSummary> {
  return jsonOrThrow<AnalyticsSummary>(await fetch(`${base(slug)}/analytics?range=${range}`));
}

/** Format a 0..1 rate as a whole-number percentage. */
export function formatRate(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

/* ----------------------------- Widget keys ------------------------------- */

export interface WidgetKey {
  id: string;
  publishableKey: string;
  allowedOrigins: string[];
  revoked: boolean;
}

export async function listWidgetKeys(slug: string): Promise<WidgetKey[]> {
  const data = await jsonOrThrow<{ keys: WidgetKey[] }>(await fetch(`${base(slug)}/widget-keys`));
  return data.keys;
}

export async function createWidgetKey(
  slug: string,
  allowedOrigins: string[],
): Promise<{ id: string; publishableKey: string }> {
  return jsonOrThrow(
    await fetch(`${base(slug)}/widget-keys`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ allowedOrigins }),
    }),
  );
}

export async function revokeWidgetKey(slug: string, id: string): Promise<void> {
  await jsonOrThrow(await fetch(`${base(slug)}/widget-keys/${id}`, { method: 'DELETE' }));
}

export interface WidgetBranding {
  greeting: string;
  assistantName: string;
  accentColor: string;
}

export async function getWidgetBranding(slug: string): Promise<WidgetBranding> {
  const data = await jsonOrThrow<{ branding: WidgetBranding }>(
    await fetch(`${base(slug)}/widget-settings`),
  );
  return data.branding;
}

export async function saveWidgetBranding(
  slug: string,
  branding: WidgetBranding,
): Promise<WidgetBranding> {
  const data = await jsonOrThrow<{ branding: WidgetBranding }>(
    await fetch(`${base(slug)}/widget-settings`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(branding),
    }),
  );
  return data.branding;
}

/* ------------------------------- Members --------------------------------- */

export type InviteRole = 'admin' | 'member';

export interface TeamMember {
  userId: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'member';
}

export interface PendingInvite {
  id: string;
  email: string;
  role: InviteRole;
  expiresAt: number;
}

export interface MembersResponse {
  members: TeamMember[];
  invitations: PendingInvite[];
}

export async function listMembers(slug: string): Promise<MembersResponse> {
  return jsonOrThrow<MembersResponse>(await fetch(`${base(slug)}/members`));
}

export async function inviteMember(
  slug: string,
  input: { email: string; role: InviteRole },
): Promise<{ id: string }> {
  return jsonOrThrow(
    await fetch(`${base(slug)}/members/invitations`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

export async function revokeInvite(slug: string, id: string): Promise<void> {
  await jsonOrThrow(await fetch(`${base(slug)}/members/invitations/${id}`, { method: 'DELETE' }));
}

export async function acceptInvitation(
  token: string,
): Promise<{ ok: boolean; slug: string | null; role: string }> {
  return jsonOrThrow(
    await fetch('/api/invitations/accept', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
    }),
  );
}

/* ----------------------------- SKU mapping ------------------------------- */

export interface MappingMember {
  id: string;
  sku: string;
  title: string;
  inStock: boolean;
  isCanonical: boolean;
}

export interface MappingGroup {
  id: string;
  confidence: number;
  reviewStatus: 'auto' | 'confirmed' | 'rejected';
  canonicalProductId: string | null;
  members: MappingMember[];
}

export async function rebuildMapping(
  slug: string,
): Promise<{ groups: number; mappedProducts: number }> {
  return jsonOrThrow(await fetch(`${base(slug)}/mapping/rebuild`, { method: 'POST' }));
}

export async function listMappingGroups(slug: string): Promise<MappingGroup[]> {
  const data = await jsonOrThrow<{ groups: MappingGroup[] }>(
    await fetch(`${base(slug)}/mapping/groups`),
  );
  return data.groups;
}

export async function setMappingReview(
  slug: string,
  groupId: string,
  reviewStatus: 'auto' | 'confirmed' | 'rejected',
): Promise<void> {
  await jsonOrThrow(
    await fetch(`${base(slug)}/mapping/groups/${groupId}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ reviewStatus }),
    }),
  );
}
